import type { Perm } from "../../auth/AuthContext";
import type { MsgKey } from "../../i18n";
import { navItem } from "../navigation";

export type GuideStage = "standards" | "review" | "operations";

export interface GuideNode {
  id: string;
  /** The sidebar destination, or null for a step the system performs on its own. */
  to: string | null;
  labelKey: MsgKey;
  descKey: MsgKey;
  perm?: Perm;
}

function menuNode(to: string, descKey: MsgKey): GuideNode {
  const item = navItem(to);
  return { id: to, to, labelKey: item.key, descKey, perm: item.perm };
}

/** Documents feeding the scope, drawn as one group because they play the same role. */
export const STANDARD_SOURCE_NODES: GuideNode[] = [
  menuNode("/policies", "sysGuideNodePoliciesDesc"),
  menuNode("/rules", "sysGuideNodeRulesDesc"),
  { ...menuNode("/educate-sage", "sysGuideNodeEducateSageDesc"), perm: "RULE_MANAGE" },
  menuNode("/dictionary", "sysGuideNodeDictionaryDesc"),
];

export const SCOPE_NODE: GuideNode = {
  ...menuNode("/sage-workflow", "sysGuideNodeScopeDesc"),
  perm: "RULE_MANAGE",
};

export const REVIEW_NODES: GuideNode[] = [
  // Before the review, not after: this is where someone checks a phrase rather
  // than finding out from a rejection.
  menuNode("/ask-sage", "sysGuideNodeAskSageDesc"),
  menuNode("/reviews", "sysGuideNodeReviewsDesc"),
  {
    id: "analysis",
    to: null,
    labelKey: "sysGuideNodeAnalysis",
    descKey: "sysGuideNodeAnalysisDesc",
  },
  menuNode("/", "sysGuideNodeDashboardDesc"),
];

export const OPERATIONS_NODES: GuideNode[] = [
  menuNode("/config", "sysGuideNodeConfigDesc"),
  menuNode("/members", "sysGuideNodeMembersDesc"),
  menuNode("/queue-health", "sysGuideNodeQueueHealthDesc"),
  menuNode("/audit", "sysGuideNodeAuditDesc"),
  menuNode("/ask-sage-log", "sysGuideNodeAskSageLogDesc"),
];

/** Tour order follows the diagram: set the standards, run a review, then operate. */
export const GUIDE_NODES: GuideNode[] = [
  ...STANDARD_SOURCE_NODES,
  SCOPE_NODE,
  ...REVIEW_NODES,
  ...OPERATIONS_NODES,
];

export interface GuideTerm {
  id: string;
  termKey: MsgKey;
  descKey: MsgKey;
}

export const GUIDE_TERMS: GuideTerm[] = [
  { id: "channel", termKey: "sysGuideTermChannel", descKey: "sysGuideTermChannelDesc" },
  { id: "category", termKey: "sysGuideTermCategory", descKey: "sysGuideTermCategoryDesc" },
  { id: "version", termKey: "sysGuideTermVersion", descKey: "sysGuideTermVersionDesc" },
  { id: "snapshot", termKey: "sysGuideTermSnapshot", descKey: "sysGuideTermSnapshotDesc" },
  { id: "revision", termKey: "sysGuideTermRevision", descKey: "sysGuideTermRevisionDesc" },
  {
    id: "approvedDocument",
    termKey: "sysGuideTermApprovedDocument",
    descKey: "sysGuideTermApprovedDocumentDesc",
  },
  { id: "taughtRule", termKey: "sysGuideTermTaughtRule", descKey: "sysGuideTermTaughtRuleDesc" },
  { id: "citation", termKey: "sysGuideTermCitation", descKey: "sysGuideTermCitationDesc" },
  {
    id: "deterministic",
    termKey: "sysGuideTermDeterministic",
    descKey: "sysGuideTermDeterministicDesc",
  },
  { id: "routing", termKey: "sysGuideTermRouting", descKey: "sysGuideTermRoutingDesc" },
  { id: "shadowMode", termKey: "sysGuideTermShadowMode", descKey: "sysGuideTermShadowModeDesc" },
  { id: "decision", termKey: "sysGuideTermDecision", descKey: "sysGuideTermDecisionDesc" },
];

/**
 * The tour points at menu links, some of which sit in a collapsed sidebar or a
 * closed top-bar dropdown, so each step asks the layout to reveal its target.
 */
export const REVEAL_NAV_TARGET_EVENT = "compliance-sage:reveal-nav-target";

export type RevealNavTargetEvent = CustomEvent<{ to: string | null }>;
